import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminBankApiConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [form, setForm] = useState({
    bankProvider: 'ICICI', environment: 'uat',
    baseUrlUat: '', baseUrlProd: '',
    payoutBaseUrlUat: '', payoutBaseUrlProd: '',
    payinPaths: [
      { name: 'QR / UPI Intent', path: '/QR3/' },
      { name: 'Transaction Status', path: '/TransactionStatus1/' },
      { name: 'Callback Status', path: '/CallbackStatus2/' },
      { name: 'Refund', path: '/Refund/' }
    ],
    payoutPaths: [
      { name: 'Create', path: '/Create' },
      { name: 'Transaction Inquiry', path: '/TransactionInquiry' },
      { name: 'Verify Beneficiary', path: '/VerifyBeneficiary/' },
      { name: 'Balance Inquiry', path: '/BalanceInquiry' },
      { name: 'Account Statement', path: '/AccountStatement' },
      { name: 'Transaction OTP', path: '/TransactionOTP' }
    ]
  });

  const getToken = () => null; // Token is now in httpOnly cookie
  const headers = () => ({}); // Cookie sent automatically via withCredentials

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get('/api/admin/bank-api-config', { headers: headers() });
      if (res.data.success) setConfigs(res.data.data || []);
    } catch (e) { console.error('Failed to fetch bank API configs:', e); }
    finally { setLoading(false); }
  };

  const openEdit = (config) => {
    // Parse dynamic paths from customPaths JSON or build from fixed fields
    let payinPaths = [];
    let payoutPaths = [];
    
    if (config.customPaths) {
      try {
        const parsed = typeof config.customPaths === 'string' ? JSON.parse(config.customPaths) : config.customPaths;
        payinPaths = parsed.payinPaths || [];
        payoutPaths = parsed.payoutPaths || [];
      } catch (e) { /* ignore parse error */ }
    }
    
    // If no dynamic paths saved yet, build from fixed fields (backward compat)
    if (payinPaths.length === 0) {
      if (config.qrPath) payinPaths.push({ name: 'QR / UPI Intent', path: config.qrPath });
      if (config.transactionStatusPath) payinPaths.push({ name: 'Transaction Status', path: config.transactionStatusPath });
      if (config.callbackStatusPath) payinPaths.push({ name: 'Callback Status', path: config.callbackStatusPath });
      if (config.refundPath) payinPaths.push({ name: 'Refund', path: config.refundPath });
    }
    if (payoutPaths.length === 0) {
      if (config.payoutPath) payoutPaths.push({ name: 'Create', path: config.payoutPath });
      if (config.payoutStatusPath) payoutPaths.push({ name: 'Transaction Inquiry', path: config.payoutStatusPath });
      if (config.verifyBeneficiaryPath) payoutPaths.push({ name: 'Verify Beneficiary', path: config.verifyBeneficiaryPath });
      if (config.balanceInquiryPath) payoutPaths.push({ name: 'Balance Inquiry', path: config.balanceInquiryPath });
      if (config.accountStatementPath) payoutPaths.push({ name: 'Account Statement', path: config.accountStatementPath });
      if (config.transactionOtpPath) payoutPaths.push({ name: 'Transaction OTP', path: config.transactionOtpPath });
    }

    setForm({
      bankProvider: config.bankProvider || 'ICICI',
      environment: config.environment || 'uat',
      baseUrlUat: config.baseUrlUat || '',
      baseUrlProd: config.baseUrlProd || '',
      payoutBaseUrlUat: config.payoutBaseUrlUat || '',
      payoutBaseUrlProd: config.payoutBaseUrlProd || '',
      payinPaths,
      payoutPaths
    });
    setEditingProvider(config.bankProvider);
  };

  const saveConfig = async () => {
    if (!form.bankProvider.trim()) { alert('Bank provider is required'); return; }
    setSaving(true);
    try {
      const payload = {
        bankProvider: form.bankProvider,
        environment: form.environment,
        baseUrlUat: form.baseUrlUat,
        baseUrlProd: form.baseUrlProd,
        payoutBaseUrlUat: form.payoutBaseUrlUat,
        payoutBaseUrlProd: form.payoutBaseUrlProd,
        customPaths: JSON.stringify({ payinPaths: form.payinPaths || [], payoutPaths: form.payoutPaths || [] }),
        // Also save to fixed fields for backward compatibility
        qrPath: (form.payinPaths || []).find(p => p.name.toLowerCase().includes('qr'))?.path || '',
        transactionStatusPath: (form.payinPaths || []).find(p => p.name.toLowerCase().includes('transaction status'))?.path || '',
        callbackStatusPath: (form.payinPaths || []).find(p => p.name.toLowerCase().includes('callback'))?.path || '',
        refundPath: (form.payinPaths || []).find(p => p.name.toLowerCase().includes('refund'))?.path || '',
        payoutPath: (form.payoutPaths || []).find(p => p.name.toLowerCase().includes('create'))?.path || '',
        payoutStatusPath: (form.payoutPaths || []).find(p => p.name.toLowerCase().includes('inquiry'))?.path || '',
        verifyBeneficiaryPath: (form.payoutPaths || []).find(p => p.name.toLowerCase().includes('verify'))?.path || '',
        balanceInquiryPath: (form.payoutPaths || []).find(p => p.name.toLowerCase().includes('balance'))?.path || '',
        accountStatementPath: (form.payoutPaths || []).find(p => p.name.toLowerCase().includes('statement'))?.path || '',
        transactionOtpPath: (form.payoutPaths || []).find(p => p.name.toLowerCase().includes('otp'))?.path || ''
      };
      await axios.post('/api/admin/bank-api-config', payload, { headers: { ...headers(), 'Content-Type': 'application/json' } });
      setEditingProvider(null);
      fetchConfigs();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }} role="status" aria-live="polite">Loading...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Bank API Configuration</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => { setEditingProvider('NEW'); setForm({ ...form, bankProvider: '' }); }}
            style={{ padding: '10px 20px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
            + Add Bank Provider
          </button>
        </div>
      </div>

      <p style={{ color: '#666', marginBottom: '20px' }}>
        Configure API base URLs and paths for each bank provider. These are shared across ALL merchants.
        Per merchant, only the Bank Merchant ID (MID), API Key, and VPA are configured.
      </p>

      {/* Edit Form */}
      {editingProvider && (
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0 }}>{editingProvider === 'NEW' ? 'Add Bank Provider' : `Edit ${editingProvider} Configuration`}</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Bank Provider *</label>
              <input type="text" value={form.bankProvider} onChange={e => setForm({...form, bankProvider: e.target.value.toUpperCase()})}
                placeholder="e.g. ICICI, EASEBUZZ, CASHFREE, RAZORPAY"
                style={inputStyle} disabled={editingProvider !== 'NEW'} list="provider-suggestions" />
              <datalist id="provider-suggestions">
                <option value="ICICI" />
                <option value="EASEBUZZ" />
                <option value="CASHFREE" />
                <option value="RAZORPAY" />
                <option value="HDFC" />
                <option value="AXIS" />
                <option value="KOTAK" />
                <option value="SBI" />
              </datalist>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Active Environment</label>
              <select value={form.environment} onChange={e => setForm({...form, environment: e.target.value})} style={inputStyle}>
                <option value="uat">UAT (Sandbox)</option>
                <option value="prod">Production</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '16px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #b8daff' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#004085' }}>Payin Base URLs</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {(form.environment === 'uat' || form.environment === '') && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>UAT Base URL {form.environment === 'uat' && '✓ Active'}</label>
                <input type="text" value={form.baseUrlUat} onChange={e => setForm({...form, baseUrlUat: e.target.value})}
                  placeholder="https://apibankingonesandbox.icicibank.com/api/MerchantAPI/UPI/v0" style={inputStyle} />
              </div>
              )}
              {(form.environment === 'prod' || form.environment === '') && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Production Base URL {form.environment === 'prod' && '✓ Active'}</label>
                <input type="text" value={form.baseUrlProd} onChange={e => setForm({...form, baseUrlProd: e.target.value})}
                  placeholder="https://apibankingone.icicibank.com/api/MerchantAPI/UPI/v0" style={inputStyle} />
              </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '16px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #a5d6a7' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#1b5e20' }}>Payout Base URLs</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {(form.environment === 'uat' || form.environment === '') && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>UAT Payout Base URL {form.environment === 'uat' && '✓ Active'}</label>
                <input type="text" value={form.payoutBaseUrlUat} onChange={e => setForm({...form, payoutBaseUrlUat: e.target.value})}
                  placeholder="https://apibankingonesandbox.icicibank.com/api/Corporate/CIB_SV/v1" style={inputStyle} />
              </div>
              )}
              {(form.environment === 'prod' || form.environment === '') && (
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Production Payout Base URL {form.environment === 'prod' && '✓ Active'}</label>
                <input type="text" value={form.payoutBaseUrlProd} onChange={e => setForm({...form, payoutBaseUrlProd: e.target.value})}
                  placeholder="https://apibankingone.icicibank.com/api/Corporate/CIB_SV/v1" style={inputStyle} />
              </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0 }}>Payin API Paths</h4>
              <button type="button" onClick={() => setForm({...form, payinPaths: [...(form.payinPaths || []), { name: '', path: '' }]})}
                style={{ padding: '4px 12px', background: '#004085', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                + Add Payin Path
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px 0' }}>
              Full URL = Payin Base URL + Path + Merchant ID
            </p>
            {(form.payinPaths || []).map((pp, i) => (
              <div key={`payin-${i}`} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                <input type="text" value={pp.name} placeholder="Name (e.g. QR, Transaction Status)"
                  onChange={e => { const arr = [...form.payinPaths]; arr[i] = {...arr[i], name: e.target.value}; setForm({...form, payinPaths: arr}); }}
                  style={{ ...inputStyle, flex: 1 }} />
                <input type="text" value={pp.path} placeholder="/QR3/"
                  onChange={e => { const arr = [...form.payinPaths]; arr[i] = {...arr[i], path: e.target.value}; setForm({...form, payinPaths: arr}); }}
                  style={{ ...inputStyle, flex: 2 }} />
                <button type="button" onClick={() => { const arr = [...form.payinPaths]; arr.splice(i, 1); setForm({...form, payinPaths: arr}); }}
                  style={{ padding: '6px 10px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
              </div>
            ))}
            {(!form.payinPaths || form.payinPaths.length === 0) && (
              <p style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>No payin paths configured. Click "+ Add Payin Path" to add.</p>
            )}
          </div>

          <div style={{ marginTop: '16px', padding: '16px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #a5d6a7' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, color: '#1b5e20' }}>Payout API Paths</h4>
              <button type="button" onClick={() => setForm({...form, payoutPaths: [...(form.payoutPaths || []), { name: '', path: '' }]})}
                style={{ padding: '4px 12px', background: '#1b5e20', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                + Add Payout Path
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px 0' }}>
              Full URL = Payout Base URL + Path
            </p>
            {(form.payoutPaths || []).map((pp, i) => (
              <div key={`payout-${i}`} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                <input type="text" value={pp.name} placeholder="Name (e.g. Create, Balance Inquiry)"
                  onChange={e => { const arr = [...form.payoutPaths]; arr[i] = {...arr[i], name: e.target.value}; setForm({...form, payoutPaths: arr}); }}
                  style={{ ...inputStyle, flex: 1 }} />
                <input type="text" value={pp.path} placeholder="/Create"
                  onChange={e => { const arr = [...form.payoutPaths]; arr[i] = {...arr[i], path: e.target.value}; setForm({...form, payoutPaths: arr}); }}
                  style={{ ...inputStyle, flex: 2 }} />
                <button type="button" onClick={() => { const arr = [...form.payoutPaths]; arr.splice(i, 1); setForm({...form, payoutPaths: arr}); }}
                  style={{ padding: '6px 10px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
              </div>
            ))}
            {(!form.payoutPaths || form.payoutPaths.length === 0) && (
              <p style={{ color: '#999', fontSize: '13px', fontStyle: 'italic' }}>No payout paths configured. Click "+ Add Payout Path" to add.</p>
            )}
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setEditingProvider(null)} style={{ padding: '10px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            <button onClick={saveConfig} disabled={saving}
              style={{ padding: '10px 24px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}

      {/* Configs Table */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} role="table">
          <caption className="sr-only">Bank API Configurations</caption>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th scope="col" style={thStyle}>Provider</th>
              <th scope="col" style={thStyle}>Environment</th>
              <th scope="col" style={thStyle}>Active Base URL</th>
              <th scope="col" style={thStyle}>Status</th>
              <th scope="col" style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.length === 0 ? (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#767676' }}>No bank API configs. Click "+ Add Bank Provider" to create one.</td></tr>
            ) : configs.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}><strong>{c.bankProvider}</strong></td>
                <td style={tdStyle}>
                  <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                    background: c.environment === 'prod' ? '#f8d7da' : '#cce5ff',
                    color: c.environment === 'prod' ? '#721c24' : '#004085' }}>
                    {c.environment?.toUpperCase()}
                  </span>
                </td>
                <td style={{ ...tdStyle, fontSize: '12px', fontFamily: 'monospace', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.environment === 'prod' ? c.baseUrlProd : c.baseUrlUat}
                </td>
                <td style={tdStyle}>
                  <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                    background: c.isActive ? '#d4edda' : '#f8d7da', color: c.isActive ? '#155724' : '#721c24' }}>
                    {c.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <button onClick={() => openEdit(c)} style={{ padding: '5px 12px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' };
const tdStyle = { padding: '12px 16px', verticalAlign: 'middle' };

export default AdminBankApiConfig;
